import React, { useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";
import { httpsCallable } from "firebase/functions";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  BellRing,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  FileText,
  Layers3,
  Play,
  Settings2,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { functions } from "../firebase";

const navItems = [
  ["Story", "#story"],
  ["Features", "#features"],
  ["PDFs", "#outputs"],
  ["ROI", "#roi"],
  ["Trial", "#trial"],
];

const painCards = [
  ["Customer changes the fabric", "Excel total is updated, but the WhatsApp quote still shows yesterday's number."],
  ["Workshop gets the old version", "Cutting, stitching, and track details travel through screenshots and memory."],
  ["Fabric gets wasted", "Roll lengths and leftovers are guessed instead of planned before production."],
  ["Follow-ups disappear", "Staff forgets which lead needs measurement, approval, advance, or installation."],
  ["Owner loses control", "Every answer depends on asking three people and scrolling through chats."],
];

const transformation = [
  ["Manual quotation", "Industry-specific quote logic"],
  ["WhatsApp follow-up", "CRM stages and reminders"],
  ["Paper workshop notes", "Auto workshop PDF"],
  ["Guessing fabric", "Roll optimizer"],
  ["Owner memory", "Dashboard visibility"],
];

const featureSections = [
  {
    id: "quotations",
    eyebrow: "Quotation engine",
    title: "Build curtain, sofa, wallpaper, and furnishing quotes without rebuilding the math every time.",
    text: "FabriQuote keeps product logic, totals, photos, GST-ready values, and customer revisions inside one quotation workspace.",
    view: "curtain",
    points: ["Curtains, blinds, sofas, wallpaper, furniture, and decor", "Customer changes without starting again", "Accurate totals and cleaner margins"],
  },
  {
    id: "crm",
    eyebrow: "CRM built into quoting",
    title: "Every enquiry, quotation, follow-up, and installation lives in the same operating flow.",
    text: "Owners can see where each deal is stuck, who is responsible, and what needs to happen next.",
    view: "crm",
    points: ["Lead tracking before a quotation exists", "Pipeline stages for real furnishing workflows", "Team assignment and next action visibility"],
  },
  {
    id: "workshop",
    eyebrow: "Workshop handoff",
    title: "Send production instructions that workshop teams can actually execute.",
    text: "Room-wise measurements, stitching logic, fabric details, and production notes turn approved quotes into clear work.",
    view: "workshop",
    points: ["Cutting and stitching references", "Less confusion between sales and workshop", "Cleaner handoff after approval"],
  },
  {
    id: "optimizer",
    eyebrow: "Roll optimizer",
    title: "Plan fabric usage before mistakes become expensive.",
    text: "The optimizer shows roll allocation, cut pieces, leftovers, and reuse visibility so every meter is treated like money.",
    view: "optimizer",
    points: ["Reduce waste with visual roll planning", "Track leftover pieces", "Download optimizer output for production"],
  },
  {
    id: "settings",
    eyebrow: "Business logic",
    title: "Configure the pricing brain of your furnishing business once.",
    text: "Stitching styles, track pairings, fullness, repeat settings, fabric widths, and business rules become reusable logic.",
    view: "settings",
    points: ["Fullness and stitching rates", "Track and curtain type logic", "Consistent calculations across staff"],
  },
];

const testimonials = [
  ["Niyas Rahman", "Curtain showroom owner", "FabriQuote feels like it was designed by someone who understands what happens after a quotation is sent."],
  ["Shruthi Menon", "Sofa and interiors company", "The proposal looks professional, but the bigger win is that the follow-up and execution stay connected."],
  ["Anand Krishnan", "Wallpaper and interior designer", "We stopped depending on memory. The CRM and saved quotes tell us exactly what is pending."],
];

const faqs = [
  ["Is FabriQuote only for curtain businesses?", "No. Curtain logic is deep, but FabriQuote also supports sofas, wallpaper, furniture, decor, blinds, and custom interiors through general quotations."],
  ["Does it replace Excel?", "Yes for the quoting and execution workflow. It keeps calculations, customer PDFs, workshop PDFs, CRM, and saved quotes connected."],
  ["Is onboarding included?", "Yes. Trial users get Zoom onboarding so they can test FabriQuote with their real workflow instead of guessing alone."],
  ["Can staff use it?", "Yes. FabriQuote supports team workflow, permissions, CRM assignment, and operational visibility."],
];

const appNavItems = ["Dashboard", "Quotes", "Saved Quotes", "CRM", "Roll Optimizer", "Stitching & Tracks", "Settings"];

const sanitizedQuoteRows = [
  ["Living W1 Main", "Curtains", "Texture Linen 09", "90.00 in", "124.00 in", "Rs. 330", "-", "Ripple - Arabian Track", "16.76 m", "Rs. 8,006"],
  ["Living W1 Sheer", "Curtains", "Linen Sheer", "90.00 in", "124.00 in", "Rs. 220", "-", "3 Pleat - M Track", "16.76 m", "Rs. 6,687"],
  ["Dining W1", "Roman Blind", "Texture Linen 09", "42.00 in", "94.50 in", "Rs. 330", "Rs. 180", "-", "2.58 m", "Rs. 3,011"],
  ["Kitchen W1", "Zebra Blind", "B70 - 14", "76.00 in", "22.00 in", "Rs. 110", "-", "-", "-", "Rs. 1,277"],
];

const crmDeals = [
  ["Client A", "Measurement", "Curtain Quote", "Rs. 65,500", "Today"],
  ["Residence Project", "Proposal Sent", "General Quote", "Rs. 42,800", "Apr 21"],
  ["Premium Client", "Workshop", "Curtain Quote", "Rs. 1,18,200", "Apr 20"],
];

const stitchingRates = [
  ["3 Pleat - M Track", "3.00", "M Track", "Rs. 330 / ft"],
  ["Ripple - Arabian Track", "2.63", "Arabian Track", "Rs. 470 / ft"],
  ["Ripple - Jumbo Track", "2.80", "Jumbo Track", "Rs. 520 / ft"],
  ["Eyelet (Custom)", "2.00", "Custom", "Rs. 410 / ft"],
];

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const DEFAULT_SALES_WHATSAPP = "917736620477";
const DEFAULT_SALES_PHONE = "+917736620477";
const DEFAULT_SALES_EMAIL = "sales@fabriquote.in";

export default function MarketingPreview() {
  const [activeTour, setActiveTour] = useState("dashboard");
  const [activeOutput, setActiveOutput] = useState("customer");
  const [trialForm, setTrialForm] = useState({
    fullName: "",
    businessName: "",
    phone: "",
    email: "",
    businessType: "Curtain showroom",
  });
  const [trialErrors, setTrialErrors] = useState({});
  const [trialSubmitState, setTrialSubmitState] = useState({
    status: "idle",
    message: "",
    requestType: "trial",
    emailSent: null,
  });
  const fullNameInputRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 45, damping: 24 });
  const smoothY = useSpring(mouseY, { stiffness: 45, damping: 24 });
  const { scrollYProgress } = useScroll();
  const progressScale = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const heroFloat = useTransform(scrollYProgress, [0, 0.3], [0, -48]);

  useEffect(() => {
    const onMove = (event) => {
      mouseX.set(event.clientX);
      mouseY.set(event.clientY);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mouseX, mouseY]);

  const tourItems = useMemo(
    () => [
      ["dashboard", "Dashboard", "dashboard"],
      ["curtain", "Curtain Quote", "curtain"],
      ["general", "General Quote", "general"],
      ["crm", "CRM", "crm"],
      ["saved", "Saved Quotes", "saved"],
      ["optimizer", "Optimizer", "optimizer"],
    ],
    []
  );

  const salesPhone = process.env.REACT_APP_SALES_PHONE || DEFAULT_SALES_PHONE;
  const salesWhatsapp = process.env.REACT_APP_SALES_WHATSAPP || DEFAULT_SALES_WHATSAPP;
  const salesEmail = process.env.REACT_APP_SALES_EMAIL || DEFAULT_SALES_EMAIL;
  const demoBookingUrl = process.env.REACT_APP_DEMO_BOOKING_URL || "";
  const salesWhatsappHref = useMemo(() => {
    if (!salesWhatsapp) return "";
    const cleanedPhone = String(salesWhatsapp).replace(/\D+/g, "");
    if (!cleanedPhone) return "";
    return `https://wa.me/${cleanedPhone}`;
  }, [salesWhatsapp]);

  const buildWhatsappMessage = (kind = "general") =>
    encodeURIComponent(
      [
        kind === "demo"
          ? "Hi FabriQuote team, I would like to book a FabriQuote demo."
          : kind === "trial"
            ? "Hi FabriQuote team, I would like to start my 10 free quotations trial."
            : "Hi FabriQuote team, I would like to know more about FabriQuote.",
        `Name: ${trialForm.fullName || "-"}`,
        `Business: ${trialForm.businessName || "-"}`,
        `Phone: ${trialForm.phone || "-"}`,
        `Email: ${trialForm.email || "-"}`,
        `Business type: ${trialForm.businessType || "-"}`,
      ].join("\n")
    );

  const getSalesWhatsappHref = (kind = "general") =>
    salesWhatsappHref ? `${salesWhatsappHref}?text=${buildWhatsappMessage(kind)}` : "";

  const scrollToTrial = () => {
    document.getElementById("trial")?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => {
      fullNameInputRef.current?.focus();
    }, 250);
  };

  const handleTrialFieldChange = (field) => (event) => {
    const value = event.target.value;
    setTrialForm((prev) => ({ ...prev, [field]: value }));
    setTrialErrors((prev) => ({ ...prev, [field]: "" }));
    if (trialSubmitState.status !== "idle") {
      setTrialSubmitState({ status: "idle", message: "", requestType: "trial", emailSent: null });
    }
  };

  const validateTrialForm = () => {
    const nextErrors = {};
    if (!trialForm.fullName.trim()) nextErrors.fullName = "Full name is required";
    if (!trialForm.businessName.trim()) nextErrors.businessName = "Business name is required";
    if (!trialForm.phone.trim()) nextErrors.phone = "Phone is required";
    if (!trialForm.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trialForm.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    setTrialErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const normalizeLeadErrorMessage = (error, requestType = "trial") => {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    if (code.includes("invalid-argument")) {
      return "Please fill all required fields.";
    }
    if (message.includes("already requested a fabriquote trial") || message.includes("already been used")) {
      return "This email or phone has already requested a FabriQuote trial. Please contact sales.";
    }
    if (code.includes("unavailable") || code.includes("network") || message.includes("network")) {
      return "Please check your internet connection and try again.";
    }
    return "We could not submit your request right now. Please WhatsApp our sales team or email sales@fabriquote.in.";
  };

  const submitLeadRequest = async (requestType = "trial") => {
    if (!validateTrialForm()) {
      if (requestType === "demo") {
        scrollToTrial();
      }
      return;
    }

    setTrialSubmitState({ status: "submitting", message: "", requestType, emailSent: null });

    try {
      const submitWebsiteLead = httpsCallable(functions, "submitWebsiteLead");
      const response = await submitWebsiteLead({
        requestType,
        ...trialForm,
        email: trialForm.email.trim(),
        phone: trialForm.phone.trim(),
        source: "fabriquote_website",
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });
      const responseStatus = response?.data?.status || "submitted";
      const emailSent = response?.data?.emailSent === true;
      const successMessage = emailSent
        ? response?.data?.message ||
          (requestType === "demo"
            ? "Demo request received. Our team has been notified and will contact you shortly to schedule your FabriQuote demo."
            : "Your request has been received. Our team has been notified and will contact you soon.")
        : requestType === "demo"
          ? "Demo request received. Our team will contact you shortly. You can also WhatsApp us directly for faster scheduling."
          : "Your request has been received. Our team will contact you soon. You can also WhatsApp us directly for faster activation.";
      setTrialSubmitState({
        status: "success",
        message: successMessage,
        requestType,
        emailSent,
      });
      setTrialErrors({});
      if (requestType === "demo" && demoBookingUrl && responseStatus !== "duplicate_trial") {
        window.open(demoBookingUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      const code = String(error?.code || "").toLowerCase();
      const message = String(error?.message || "").toLowerCase();
      const shouldTryLegacyFallback =
        code.includes("not-found") ||
        code.includes("unimplemented") ||
        code.includes("internal") ||
        message.includes("internal") ||
        message.includes("not found");

      if (shouldTryLegacyFallback) {
        try {
          const legacyTrialRequest = httpsCallable(functions, "submitMarketingTrialRequest");
          await legacyTrialRequest({
            requestType,
            ...trialForm,
            email: trialForm.email.trim(),
            phone: trialForm.phone.trim(),
            source: "fabriquote_website",
            pageUrl: window.location.href,
            userAgent: navigator.userAgent,
          });
          setTrialSubmitState({
            status: "success",
            message:
              requestType === "demo"
                ? "Demo request received. Our team will contact you shortly. You can also WhatsApp us directly for faster scheduling."
                : "Your request has been received. Our team will contact you soon. You can also WhatsApp us directly for faster activation.",
            requestType,
            emailSent: false,
          });
          setTrialErrors({});
          return;
        } catch (legacyError) {
          setTrialSubmitState({
            status: "error",
            message: normalizeLeadErrorMessage(legacyError, requestType),
            requestType,
            emailSent: null,
          });
          return;
        }
      }

      setTrialSubmitState({
        status: "error",
        message: normalizeLeadErrorMessage(error, requestType),
        requestType,
        emailSent: null,
      });
    }
  };

  const submitTrialRequest = async (event) => {
    event.preventDefault();
    await submitLeadRequest("trial");
  };

  const openBookDemo = async () => {
    if (
      !trialForm.fullName.trim() &&
      !trialForm.businessName.trim() &&
      !trialForm.phone.trim() &&
      !trialForm.email.trim()
    ) {
      scrollToTrial();
      setTrialSubmitState({
        status: "error",
        message: "Please fill your details below so we can schedule your FabriQuote demo.",
        requestType: "demo",
        emailSent: null,
      });
      return;
    }

    await submitLeadRequest("demo");
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#020712] text-white">
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-50 h-1 bg-gradient-to-r from-cyan-300 via-blue-400 to-indigo-500"
        style={{ width: progressScale }}
      />
      <motion.div
        className="pointer-events-none fixed z-0 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400/10 blur-[90px]"
        style={{ left: smoothX, top: smoothY }}
      />
      <LuxuryBackground />
      <Nav scrollToTrial={scrollToTrial} />

      <main className="relative z-10">
        <section id="top" className="mx-auto grid min-h-[auto] w-full max-w-[1480px] items-center gap-14 px-5 pb-24 pt-32 sm:px-8 sm:pb-28 sm:pt-40 lg:min-h-[980px] lg:grid-cols-[0.88fr_1.12fr] lg:px-10">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={reveal} className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-cyan-200">
              <Sparkles className="h-4 w-4" />
              Built for furnishing businesses
            </motion.div>
            <motion.h1 variants={reveal} className="max-w-4xl text-[clamp(2.7rem,10vw,7.8rem)] font-black leading-[0.9] tracking-[-0.08em] sm:leading-[0.88]">
              Run your furnishing business on software built for how you actually work.
            </motion.h1>
            <motion.p variants={reveal} className="mt-8 max-w-2xl text-base leading-8 text-slate-300 sm:text-xl sm:leading-9">
              FabriQuote connects quotations, CRM, follow-ups, customer PDFs, workshop PDFs, roll optimization, installation tracking, stock, and reporting in one premium operating system.
            </motion.p>
            <motion.div variants={reveal} className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={scrollToTrial}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-200 via-sky-300 to-blue-500 px-7 py-4 font-extrabold text-slate-950 shadow-[0_24px_60px_rgba(56,189,248,0.28)] transition hover:-translate-y-1"
              >
                Start Free Trial <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={openBookDemo}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.06] px-7 py-4 font-bold text-white backdrop-blur-xl transition hover:-translate-y-1 hover:border-cyan-200/40"
              >
                <Play className="h-4 w-4" /> Book Zoom Demo
              </button>
            </motion.div>
            <motion.div variants={reveal} className="mt-8 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
              {["10 free quotations included", "Zoom onboarding included", "Built for curtains, sofas, blinds, wallpaper", "No generic setup maze"].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.045] px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-cyan-300" />
                  {item}
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div style={{ y: heroFloat }} className="relative min-h-[420px] sm:min-h-[560px] lg:min-h-[720px]">
            <HeroProductStack />
          </motion.div>
        </section>

        <PainSection />
        <TransformationSection />
        <ProductTour tourItems={tourItems} activeTour={activeTour} setActiveTour={setActiveTour} />
        <FeatureSections />
        <OutputShowcase activeOutput={activeOutput} setActiveOutput={setActiveOutput} />
        <GenericCrmSection />
        <RoiSection />
        <Testimonials />
        <TrialSection
          trialForm={trialForm}
          trialErrors={trialErrors}
          trialSubmitState={trialSubmitState}
          handleTrialFieldChange={handleTrialFieldChange}
          submitTrialRequest={submitTrialRequest}
          openBookDemo={openBookDemo}
          fullNameInputRef={fullNameInputRef}
          salesWhatsappHref={salesWhatsappHref}
          salesWhatsappMessageHref={getSalesWhatsappHref(trialSubmitState.requestType)}
          salesEmail={salesEmail}
          salesPhone={salesPhone}
        />
        <FAQ />
        <FinalCTA
          scrollToTrial={scrollToTrial}
          openBookDemo={openBookDemo}
          salesPhone={salesPhone}
          salesEmail={salesEmail}
        />
      </main>
      <FloatingWhatsappButton href={getSalesWhatsappHref("general")} />
    </div>
  );
}

function Nav({ scrollToTrial }) {
  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-[#020712]/70 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-[1480px] items-center justify-between px-5 py-4 sm:px-8 lg:px-10">
        <a href="#top" className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-cyan-300 to-blue-600 font-black text-slate-950 shadow-[0_16px_50px_rgba(56,189,248,0.35)]">FQ</div>
          <div>
            <div className="text-lg font-black tracking-[-0.04em]">FabriQuote</div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Operating system</div>
          </div>
        </a>
        <nav className="hidden items-center gap-6 lg:flex">
          {navItems.map(([label, href]) => (
            <a key={label} href={href} className="text-sm font-semibold text-slate-300 transition hover:text-white">{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm font-semibold text-slate-400 xl:inline">WhatsApp: +91 77366 20477</span>
          <a href="https://quote.fabriquote.in/login" className="hidden text-sm font-semibold text-slate-300 hover:text-white sm:inline">Login</a>
          <button type="button" onClick={scrollToTrial} className="rounded-full bg-white px-5 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-100">Start Free Trial</button>
        </div>
      </div>
    </header>
  );
}

function LuxuryBackground() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(56,189,248,0.16),transparent_34%),radial-gradient(circle_at_86%_18%,rgba(79,70,229,0.18),transparent_34%),linear-gradient(180deg,#020712_0%,#030915_45%,#050816_100%)]" />
      <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:64px_64px]" />
      <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_center,white_1px,transparent_1px)] [background-size:18px_18px]" />
      <div className="absolute left-1/2 top-[980px] h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-blue-500/10 blur-[120px]" />
    </div>
  );
}

function SectionHeader({ eyebrow, title, text, center = false }) {
  return (
    <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.35 }} className={center ? "mx-auto max-w-4xl text-center" : "max-w-4xl"}>
      <p className="mb-5 text-xs font-black uppercase tracking-[0.24em] text-cyan-300">{eyebrow}</p>
      <h2 className="text-[clamp(2.4rem,5vw,5.4rem)] font-black leading-[0.92] tracking-[-0.07em]">{title}</h2>
      {text ? <p className="mt-6 text-base leading-8 text-slate-300 sm:text-lg">{text}</p> : null}
    </motion.div>
  );
}

function GlassCard({ children, className = "", onClick }) {
  return (
    <motion.div
      variants={reveal}
      whileHover={{ y: onClick ? -5 : -2 }}
      onClick={onClick}
      className={`rounded-[32px] border border-white/10 bg-white/[0.055] shadow-[0_30px_90px_rgba(0,0,0,0.35)] backdrop-blur-2xl ${onClick ? "cursor-pointer" : ""} ${className}`}
    >
      {children}
    </motion.div>
  );
}

function BrowserFrame({ children, className = "" }) {
  return (
    <motion.div
      whileHover={{ y: -6, rotateX: 1.2, rotateY: -1.4 }}
      transition={{ type: "spring", stiffness: 150, damping: 20 }}
      className={`overflow-hidden rounded-[30px] border border-cyan-200/15 bg-[#07111f] shadow-[0_34px_110px_rgba(0,0,0,0.55),0_0_80px_rgba(56,189,248,0.12)] ${className}`}
    >
      <div className="flex h-10 items-center gap-2 border-b border-white/10 bg-white/[0.045] px-3 sm:h-11 sm:px-4">
        <span className="h-3 w-3 rounded-full bg-red-400/80" />
        <span className="h-3 w-3 rounded-full bg-amber-300/80" />
        <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
        <span className="ml-auto hidden text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 sm:block">quote.fabriquote.in</span>
      </div>
      {children}
    </motion.div>
  );
}

function AppScreen({ type = "dashboard", compact = false }) {
  const activeLabel = {
    dashboard: "Dashboard",
    curtain: "Quotes",
    general: "Quotes",
    crm: "CRM",
    saved: "Saved Quotes",
    optimizer: "Roll Optimizer",
    workshop: "Quotes",
    settings: "Stitching & Tracks",
  }[type] || "Dashboard";

  return (
    <div className="h-full min-h-full overflow-auto bg-[#eef3f9] text-slate-950">
      <div className={`grid h-full min-w-[720px] ${compact ? "grid-cols-[72px_1fr] sm:grid-cols-[86px_1fr]" : "grid-cols-[72px_1fr] sm:grid-cols-[86px_1fr] lg:grid-cols-[210px_1fr]"}`}>
        <aside className="border-r border-slate-200 bg-white p-2.5 sm:p-4">
          <div className="mb-5 flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-xs font-black text-white">FQ</div>
            {!compact ? (
              <div>
                <div className="text-base font-black tracking-[-0.04em]">FabriQuote</div>
                <div className="text-[11px] font-semibold text-slate-500">Quotation workspace</div>
              </div>
            ) : null}
          </div>
          <div className="grid gap-2">
            {appNavItems.map((item) => {
              const active = item === activeLabel || (activeLabel === "Quotes" && item === "Quotes");
              return (
                <div key={item} className={`flex items-center gap-3 rounded-xl px-2 py-2.5 text-[11px] font-bold sm:px-3 ${active ? "bg-blue-50 text-blue-700" : "text-slate-500"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${active ? "bg-blue-500" : "bg-slate-300"}`} />
                  {!compact && <span className="hidden lg:inline">{item}</span>}
                </div>
              );
            })}
          </div>
        </aside>
        <main className="overflow-hidden p-3 sm:p-5 lg:p-7">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Demo workspace</div>
              <h4 className="mt-1 text-lg font-black tracking-[-0.05em] sm:text-2xl">{screenTitle(type)}</h4>
            </div>
            <div className="hidden rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 sm:block">Quote #FQ-2026-041</div>
          </div>
          {renderScreen(type)}
        </main>
      </div>
    </div>
  );
}

function screenTitle(type) {
  return {
    dashboard: "Business Dashboard",
    curtain: "Curtain quotation",
    general: "General quotation",
    crm: "CRM pipeline",
    saved: "Saved quotations",
    optimizer: "Roll optimizer",
    workshop: "Workshop handoff",
    settings: "Stitching & Tracks",
  }[type] || "Business Dashboard";
}

function renderScreen(type) {
  switch (type) {
    case "curtain":
      return <CurtainQuoteScreen />;
    case "general":
      return <GeneralQuoteScreen />;
    case "crm":
      return <CrmScreen />;
    case "saved":
      return <SavedQuotesScreen />;
    case "optimizer":
      return <OptimizerScreen />;
    case "workshop":
      return <WorkshopScreen />;
    case "settings":
      return <SettingsScreen />;
    default:
      return <DashboardScreen />;
  }
}

function DashboardScreen() {
  return (
    <div className="grid gap-5">
      <div className="grid gap-3 md:grid-cols-4">
        {[["Active Quotes", "42"], ["Pipeline Value", "Rs. 8.4L"], ["Pending Installs", "7"], ["This Month", "Rs. 2.1L"]].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-black tracking-[-0.05em]">{value}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between">
            <h5 className="font-black">Recent quotation movement</h5>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">Live</span>
          </div>
          <div className="flex h-48 items-end gap-3">
            {[44, 72, 56, 86, 68, 92, 76, 104].map((height, index) => (
              <div key={index} className="flex-1 rounded-t-xl bg-gradient-to-t from-blue-700 to-cyan-300" style={{ height }} />
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h5 className="font-black">Upcoming installations</h5>
          <div className="mt-4 grid gap-3">
            {["Client A - Living curtains", "Residence Project - Wallpaper", "Premium Client - Master bedroom"].map((item, index) => (
              <div key={item} className="flex items-center justify-between rounded-2xl bg-slate-50 p-3 text-sm">
                <span className="font-bold">{item}</span>
                <span className="text-xs font-black text-blue-700">Apr {24 + index}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CurtainQuoteScreen() {
  return (
    <div className="grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-3">
          <MiniField label="Customer" value="Client A" />
          <MiniField label="Room" value="Living + Dining" />
          <MiniField label="Gross Total" value="Rs. 65,500" highlight />
        </div>
        <RealQuoteTable className="mt-5" />
      </div>
      <div className="grid gap-5">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h5 className="font-black">Quote logic highlights</h5>
          <div className="mt-4 grid gap-3">
            {[["Ripple ratio", "2.63x"], ["Pleat type", "Ripple fold"], ["Track handling", "Arabian Track"], ["GST", "Applied"]].map(([label, value]) => (
              <div key={label} className="flex justify-between rounded-2xl bg-slate-50 p-3 text-sm">
                <span className="font-bold">{label}</span>
                <span className="font-black text-blue-700">{value}</span>
              </div>
            ))}
          </div>
        </div>
        <SettingsScreen condensed />
      </div>
    </div>
  );
}

function GeneralQuoteScreen() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <MiniField label="Quote from" value="FabriQuote Studio" />
        <MiniField label="Quote for" value="Residence Project" />
        <MiniField label="GST" value="Enabled" />
        <MiniField label="Total" value="Rs. 42,800" highlight />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {[
          ["Sofa reupholstery", "Premium boucle fabric", "1 set", "Rs. 22,500"],
          ["Wallpaper accent wall", "Textured vinyl", "320 sqft", "Rs. 14,400"],
          ["Decor installation", "Hardware + labour", "1 job", "Rs. 5,900"],
        ].map(([title, desc, qty, amount]) => (
          <div key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 h-24 rounded-xl bg-gradient-to-br from-slate-200 to-blue-100" />
            <div className="font-black">{title}</div>
            <div className="mt-1 text-xs text-slate-500">{desc}</div>
            <div className="mt-4 flex justify-between text-sm font-black"><span>{qty}</span><span>{amount}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrmScreen() {
  const columns = ["New Enquiry", "Measurement", "Proposal Sent", "Workshop"];
  return (
    <div className="grid gap-4 lg:grid-cols-4">
      {columns.map((column, colIndex) => (
        <div key={column} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h5 className="text-sm font-black">{column}</h5>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-500">{colIndex + 2}</span>
          </div>
          <div className="grid gap-3">
            {crmDeals.slice(0, colIndex === 3 ? 1 : 2).map(([client, stage, type, value, date]) => (
              <div key={`${column}-${client}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="font-black">{client}</div>
                <div className="mt-1 text-[11px] font-semibold text-slate-500">{type} / {stage}</div>
                <div className="mt-4 flex justify-between text-xs font-black"><span className="text-blue-700">{value}</span><span>{date}</span></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SavedQuotesScreen() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black text-slate-500">Search saved quotations</div>
        <div className="flex gap-2 text-xs font-black">
          <span className="rounded-full bg-blue-50 px-3 py-2 text-blue-700">All</span>
          <span className="rounded-full bg-slate-100 px-3 py-2 text-slate-500">Draft</span>
          <span className="rounded-full bg-slate-100 px-3 py-2 text-slate-500">Final</span>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200">
        {crmDeals.map(([client, stage, type, value, date], index) => (
          <div key={client} className={`grid grid-cols-[1fr_0.8fr_0.8fr_0.7fr] gap-3 px-4 py-4 text-sm ${index % 2 ? "bg-slate-50" : "bg-white"}`}>
            <span className="font-black">{client}</span>
            <span className="text-slate-500">{type}</span>
            <span className="font-black">{value}</span>
            <span className="text-right text-slate-500">{date}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkshopScreen() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h5 className="font-black">Workshop Sheet - Curtains & Romans</h5>
          <p className="mt-1 text-xs font-semibold text-slate-500">Client A / Quote FQ-2026-041 / Ready for cutting</p>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700">Linked lifecycle</span>
      </div>
      <div className="grid gap-3">
        {[
          ["Living W1 Main", "Curtains", "Ripple - Arabian Track", "90 x 124 in", "5.0 pcs", "New Cut"],
          ["Living W1 Sheer", "Curtains", "3 Pleat - M Track", "90 x 124 in", "5.0 pcs", "New Cut"],
          ["Dining W1", "Roman Blind", "-", "42 x 94.5 in", "1.0 pcs", "New Cut"],
        ].map((row) => (
          <div key={row[0]} className="grid grid-cols-[1fr_0.8fr_1fr_0.8fr_0.65fr_0.75fr] gap-3 rounded-2xl bg-slate-50 p-3 text-xs">
            {row.map((cell) => <span key={cell} className="font-bold text-slate-700">{cell}</span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function OptimizerScreen() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <MiniField label="Fabric" value="Texture Linen 09" />
        <MiniField label="Required" value="41.80 m" />
        <MiniField label="Reusable" value="5.80 m" />
        <MiniField label="Waste" value="1.90 m" highlight />
      </div>
      {["Roll A - 30m", "Roll B - 22m", "Half-piece reuse"].map((roll, index) => (
        <div key={roll} className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex justify-between text-sm font-black"><span>{roll}</span><span className="text-blue-700">{index === 2 ? "Reused 1 piece" : "Leftover planned"}</span></div>
          <div className="flex h-14 overflow-hidden rounded-xl bg-slate-200">
            {[34, 24, 18, 12].map((width, idx) => (
              <div key={idx} className={["bg-blue-700", "bg-cyan-500", "bg-indigo-500", "bg-emerald-500"][idx]} style={{ width: `${width}%` }} />
            ))}
            <div className="flex-1 bg-slate-300" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsScreen({ condensed = false }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h5 className="font-black">Stitching & Track Rates</h5>
        {!condensed ? <span className="rounded-full bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">Add New Stitching Type</span> : null}
      </div>
      <div className="grid gap-2">
        {stitchingRates.slice(0, condensed ? 3 : 4).map(([name, fullness, track, rate]) => (
          <div key={name} className="grid grid-cols-[1.2fr_0.55fr_0.9fr_0.75fr] gap-2 rounded-2xl bg-slate-50 p-3 text-xs">
            <span className="font-black">{name}</span>
            <span>Fullness {fullness}</span>
            <span>{track}</span>
            <span className="text-right font-black text-blue-700">{rate}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RealQuoteTable({ className = "" }) {
  return (
    <div className={`overflow-x-auto rounded-2xl border border-slate-200 ${className}`}>
      <div className="min-w-[860px]">
        <div className="grid grid-cols-[1fr_0.75fr_0.9fr_0.65fr_0.65fr_0.65fr_0.65fr_1fr_0.65fr_0.75fr] bg-blue-800 px-3 py-3 text-[10px] font-black uppercase tracking-[0.1em] text-white">
          {["Item", "Product", "Fabric", "W", "H", "MRP/m", "Rate/sqft", "Stitch", "Fabric", "Total"].map((head) => <span key={head}>{head}</span>)}
        </div>
        {sanitizedQuoteRows.map((row, index) => (
          <div key={row[0]} className={`grid grid-cols-[1fr_0.75fr_0.9fr_0.65fr_0.65fr_0.65fr_0.65fr_1fr_0.65fr_0.75fr] gap-1 px-3 py-3 text-[11px] ${index % 2 ? "bg-slate-50" : "bg-white"}`}>
            {row.map((cell, cellIndex) => <span key={`${row[0]}-${cellIndex}`} className={cellIndex === 0 || cellIndex === 9 ? "font-black" : "text-slate-600"}>{cell}</span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniField({ label, value, highlight = false }) {
  return (
    <div className={`rounded-2xl border p-3 ${highlight ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`mt-1 text-sm font-black ${highlight ? "text-blue-800" : "text-slate-950"}`}>{value}</div>
    </div>
  );
}

function HeroProductStack() {
  const cards = [
    [Users, "CRM", "27 active deals", "left-0 top-10 sm:top-16"],
    [FileText, "Quotation", "Rs. 65,500 synced", "right-0 top-0 sm:right-3 sm:top-5"],
    [Layers3, "Roll Optimizer", "14.2m saved", "left-3 bottom-16 sm:left-8 sm:bottom-20"],
    [ClipboardList, "Workshop PDF", "Ready for cutting", "right-0 bottom-20 sm:bottom-28"],
    [CalendarDays, "Installations", "6 due this week", "left-1/2 top-[46%] -translate-x-1/2 sm:top-[49%]"],
  ];

  return (
    <div className="relative h-full min-h-[420px] sm:min-h-[560px] lg:min-h-[720px]">
      <motion.div
        animate={{ y: [0, -16, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-x-0 top-6 mx-auto w-[min(100%,860px)] sm:top-12 lg:top-16"
      >
        <BrowserFrame className="h-[340px] rotate-0 sm:h-[430px] sm:rotate-[-1deg] lg:h-[560px] lg:rotate-[-1.5deg]">
          <AppScreen type="dashboard" compact />
        </BrowserFrame>
      </motion.div>
      <motion.div
        animate={{ y: [0, 18, 0] }}
        transition={{ duration: 8.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-2 right-0 hidden w-[58%] max-w-[500px] sm:block sm:right-2 lg:bottom-4 lg:right-6"
      >
        <BrowserFrame className="h-[285px] rotate-[3deg]">
          <AppScreen type="crm" compact />
        </BrowserFrame>
      </motion.div>
      <motion.div
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 hidden w-[45%] max-w-[390px] md:block"
      >
        <BrowserFrame className="h-[250px] rotate-[-4deg]">
          <AppScreen type="optimizer" compact />
        </BrowserFrame>
      </motion.div>
      {cards.map(([Icon, title, detail, position], index) => (
        <motion.div
          key={title}
          animate={{ y: [0, index % 2 ? 10 : -10, 0] }}
          transition={{ duration: 5 + index, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute ${position} z-20 rounded-2xl border border-white/12 bg-[#07111f]/80 p-3 shadow-[0_22px_70px_rgba(0,0,0,0.42)] backdrop-blur-2xl ${index > 1 ? "hidden sm:block" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-300/12 text-cyan-200 sm:h-10 sm:w-10">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <div>
              <div className="text-xs font-black sm:text-sm">{title}</div>
              <div className="mt-0.5 text-[10px] text-slate-400 sm:text-xs">{detail}</div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function PainSection() {
  return (
    <section id="story" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader
        eyebrow="The daily chaos"
        title="Furnishing businesses are not losing money because they cannot make a quote."
        text="They lose money when the quote changes, the follow-up is missed, the workshop receives the wrong version, or the owner cannot see what is really happening."
      />
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.16 }} className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
        {painCards.map(([title, text], index) => (
          <GlassCard key={title} className="relative min-h-[250px] p-6">
            <div className="mb-7 inline-flex rounded-full bg-orange-500/12 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-orange-300">Problem {index + 1}</div>
            <h3 className="text-2xl font-black tracking-[-0.04em]">{title}</h3>
            <p className="mt-4 text-sm leading-7 text-slate-300">{text}</p>
            <div className="absolute bottom-5 right-5 h-16 w-16 rounded-full bg-orange-400/10 blur-2xl" />
          </GlassCard>
        ))}
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="mt-14 rounded-[42px] border border-cyan-300/20 bg-cyan-300/10 p-8 text-center shadow-[0_0_100px_rgba(56,189,248,0.12)]">
        <p className="text-sm font-black uppercase tracking-[0.24em] text-cyan-200">FabriQuote fixes this.</p>
        <h3 className="mx-auto mt-4 max-w-4xl text-[clamp(2rem,4vw,4rem)] font-black leading-[0.95] tracking-[-0.06em]">
          One connected workflow from enquiry to quotation to workshop to installation.
        </h3>
      </motion.div>
    </section>
  );
}

function TransformationSection() {
  return (
    <section className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader
        eyebrow="Before and after"
        title="The same business, finally organized."
        text="FabriQuote turns disconnected fragments into a controlled operating system for furnishing work."
        center
      />
      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-7 sm:p-9">
          <h3 className="text-3xl font-black tracking-[-0.05em] text-orange-200">Without FabriQuote</h3>
          <div className="mt-7 grid gap-4">
            {transformation.map(([before], index) => (
              <motion.div key={before} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }} className="flex items-center gap-4 rounded-2xl border border-orange-300/12 bg-orange-500/8 p-4">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-orange-500/15 text-sm font-black text-orange-200">!</span>
                <span className="font-bold text-slate-200">{before}</span>
              </motion.div>
            ))}
          </div>
        </GlassCard>
        <GlassCard className="p-7 sm:p-9">
          <h3 className="text-3xl font-black tracking-[-0.05em] text-cyan-200">With FabriQuote</h3>
          <div className="mt-7 grid gap-4">
            {transformation.map(([, after], index) => (
              <motion.div key={after} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }} className="flex items-center gap-4 rounded-2xl border border-cyan-300/15 bg-cyan-300/8 p-4">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-cyan-300/15 text-cyan-200"><CheckCircle2 className="h-5 w-5" /></span>
                <span className="font-bold text-slate-100">{after}</span>
              </motion.div>
            ))}
          </div>
        </GlassCard>
      </div>
    </section>
  );
}

function ProductTour({ tourItems, activeTour, setActiveTour }) {
  const active = tourItems.find(([key]) => key === activeTour) || tourItems[0];
  return (
    <section id="tour" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader
        eyebrow="Product tour"
        title="A real software showcase, not a decorative mockup."
        text="Screenshots are framed large, bright, and readable so visitors can immediately trust that this is real operational software."
      />
      <div className="mt-10 flex gap-3 overflow-x-auto pb-3">
        {tourItems.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setActiveTour(key)} className={`shrink-0 rounded-full border px-5 py-3 text-sm font-bold transition ${activeTour === key ? "border-cyan-300/40 bg-cyan-300/12 text-white" : "border-white/10 bg-white/[0.045] text-slate-300 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-8 rounded-[42px] border border-white/10 bg-white/[0.045] p-4 backdrop-blur-2xl sm:p-6 lg:grid-cols-[0.35fr_0.65fr] lg:p-8">
        <div className="flex flex-col justify-center p-3 sm:p-5">
          <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">{active[1]}</p>
          <h3 className="mt-5 text-[clamp(2rem,6vw,3rem)] font-black leading-[0.95] tracking-[-0.06em]">{active[1]} that matches real furnishing operations.</h3>
          <p className="mt-6 text-base leading-8 text-slate-300">From saved quotes to CRM to optimizer, every screen supports the same connected workflow instead of forcing owners to manage separate tools.</p>
        </div>
        <BrowserFrame className="h-[380px] sm:h-[430px] lg:h-[clamp(430px,58vw,760px)]">
          <AppScreen type={active[2]} />
        </BrowserFrame>
      </div>
    </section>
  );
}

function FeatureSections() {
  return (
    <section id="features" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader eyebrow="Every major problem, solved" title="Built for the work after the customer says, “change this item.”" />
      <div className="mt-16 grid gap-24">
        {featureSections.map((feature, index) => (
          <motion.div
            key={feature.id}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.18 }}
            className={`grid items-center gap-10 lg:grid-cols-2 ${index % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <div>
              <p className="mb-5 text-xs font-black uppercase tracking-[0.24em] text-cyan-300">{feature.eyebrow}</p>
              <h3 className="text-[clamp(2.2rem,4.6vw,4.7rem)] font-black leading-[0.94] tracking-[-0.07em]">{feature.title}</h3>
              <p className="mt-6 text-base leading-8 text-slate-300">{feature.text}</p>
              <div className="mt-8 grid gap-3">
                {feature.points.map((point) => (
                  <div key={point} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.045] p-4 text-slate-200">
                    <CheckCircle2 className="h-5 w-5 text-cyan-300" />
                    <span className="font-semibold">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <BrowserFrame className="h-[360px] sm:h-[420px] lg:h-[clamp(420px,48vw,660px)]">
              <AppScreen type={feature.view} />
            </BrowserFrame>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function OutputShowcase({ activeOutput, setActiveOutput }) {
  const previews = {
    customer: ["Customer PDF", <CustomerPdfPreview />],
    workshop: ["Workshop PDF", <WorkshopPdfPreview />],
    optimizer: ["Roll Optimizer PDF", <OptimizerPdfPreview />],
  };
  const active = previews[activeOutput];

  return (
    <section id="outputs" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader
        eyebrow="PDF outputs"
        title="No blank boxes. Realistic outputs your team and customers can understand."
        text="Customer PDFs, workshop PDFs, and optimizer previews are rendered as rich panels with believable furnishing data."
      />
      <div className="mt-12 grid gap-8 lg:grid-cols-[0.32fr_0.68fr]">
        <div className="grid content-start gap-4">
          {Object.entries(previews).map(([key, [label]]) => (
            <button key={key} type="button" onClick={() => setActiveOutput(key)} className={`rounded-[28px] border p-6 text-left transition hover:-translate-y-1 ${activeOutput === key ? "border-cyan-300/35 bg-cyan-300/12" : "border-white/10 bg-white/[0.045]"}`}>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Preview</p>
              <h3 className="mt-3 text-3xl font-black tracking-[-0.05em]">{label}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-300">Open a realistic output preview with sample furnishing data.</p>
            </button>
          ))}
        </div>
        <BrowserFrame className="min-h-[520px] bg-[#edf3fb] sm:min-h-[620px] lg:min-h-[720px]">
          <div className="h-full min-h-[480px] bg-[#edf3fb] p-3 text-slate-900 sm:min-h-[560px] sm:p-6 lg:min-h-[680px] lg:p-8">{active[1]}</div>
        </BrowserFrame>
      </div>
    </section>
  );
}

function CustomerPdfPreview() {
  return (
    <div className="mx-auto max-w-[880px] rounded-[24px] bg-white p-4 shadow-2xl sm:p-6 lg:p-8">
      <div className="flex items-start justify-between gap-5 border-b border-slate-200 pb-6">
        <div>
          <div className="grid h-14 w-40 place-items-center rounded-2xl bg-slate-100 text-2xl font-black tracking-[-0.06em] text-blue-800">FQ Studio</div>
          <p className="mt-3 text-sm leading-6 text-slate-500">Sample furnishing studio<br />Address hidden for preview</p>
        </div>
        <div className="text-right text-sm leading-6">
          <div className="font-black">Customer Quotation</div>
          <div className="text-slate-500">Quote No. FQ-2026-041</div>
          <div className="text-slate-500">22 April 2026</div>
        </div>
      </div>
      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        <Info label="Customer" value="Client A" />
        <Info label="Contact" value="Hidden in marketing preview" />
        <Info label="Project" value="Residence Project" />
        <Info label="Prepared by" value="FabriQuote demo workspace" />
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200">
        <div className="min-w-[860px]">
          <div className="grid grid-cols-[1fr_0.8fr_0.9fr_0.55fr_0.55fr_0.7fr_0.7fr_0.9fr_0.65fr_0.75fr] bg-blue-800 px-3 py-3 text-[10px] font-black uppercase tracking-[0.1em] text-white">
            {["Item", "Product", "Fabric", "W", "H", "MRP/m", "Rate/sqft", "Stitch", "Fabric", "Total"].map((head) => <span key={head}>{head}</span>)}
          </div>
          {sanitizedQuoteRows.map((row, index) => (
            <div key={row[0]} className={`grid grid-cols-[1fr_0.8fr_0.9fr_0.55fr_0.55fr_0.7fr_0.7fr_0.9fr_0.65fr_0.75fr] gap-1 border-t border-slate-200 px-3 py-3 text-[10px] ${index % 2 ? "bg-slate-50" : "bg-white"}`}>
              {row.map((cell, cellIndex) => <span key={`${row[0]}-${cellIndex}`} className={cellIndex === 0 || cellIndex === 9 ? "font-black text-slate-950" : "text-slate-600"}>{cell}</span>)}
            </div>
          ))}
        </div>
      </div>
      <div className="ml-auto mt-7 max-w-sm rounded-2xl bg-slate-50 p-5">
        <div className="flex justify-between py-2 text-sm"><span>Subtotal</span><strong>Rs. 61,775</strong></div>
        <div className="flex justify-between py-2 text-sm"><span>Discount</span><strong>Rs. 1,275</strong></div>
        <div className="flex justify-between py-2 text-sm"><span>GST</span><strong>Rs. 5,000</strong></div>
        <div className="flex justify-between border-t border-slate-200 pt-4 text-xl font-black"><span>Grand Total</span><span>Rs. 65,500</span></div>
      </div>
      <div className="mt-10 flex flex-col items-start justify-between gap-6 text-sm text-slate-500 sm:flex-row sm:items-end">
        <p>Valid for 15 days. Measurements, fabric availability, and installation schedule are confirmed after approval.</p>
        <div className="w-44 border-t border-slate-300 pt-2 text-center">Customer Signature</div>
      </div>
    </div>
  );
}

function WorkshopPdfPreview() {
  const rows = [
    ["Living W1 Main", "Curtains", "Texture Linen 09", "Ripple - Arabian Track", "90.00 in", "124.00 in", "5.0 pcs", "16.77 m"],
    ["Living W1 Sheer", "Curtains", "Linen Sheer", "3 Pleat - M Track", "90.00 in", "124.00 in", "5.0 pcs", "16.77 m"],
    ["Dining W1", "Roman Blind", "Texture Linen 09", "-", "42.00 in", "94.50 in", "1.0 pcs", "2.60 m"],
  ];
  return (
    <div className="mx-auto max-w-[900px] rounded-[24px] bg-white p-4 shadow-2xl sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">Workshop Sheet - Curtains & Romans</div>
          <h3 className="mt-2 text-3xl font-black tracking-[-0.05em]">Client A / Residence Project</h3>
          <p className="mt-1 text-slate-500">Quote FQ-2026-041 / Date hidden in preview</p>
        </div>
        <div className="rounded-2xl bg-blue-50 px-5 py-4 text-sm font-bold text-blue-900">Powered by FabriQuote</div>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Info label="Half Pieces Saved" value="2" />
        <Info label="Half Pieces Reused" value="1" />
        <Info label="Lifecycle" value="New cut + reuse tracking" />
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[1fr_0.7fr_0.85fr_0.95fr_0.65fr_0.65fr_0.65fr_0.7fr] bg-blue-800 px-3 py-3 text-[10px] font-black uppercase tracking-[0.1em] text-white">
            {["Item", "Product", "Fabric", "Stitch", "W", "H", "Cutting", "New Fabric"].map((head) => <span key={head}>{head}</span>)}
          </div>
          {rows.map((row, index) => (
            <div key={row[0]} className={`grid grid-cols-[1fr_0.7fr_0.85fr_0.95fr_0.65fr_0.65fr_0.65fr_0.7fr] gap-1 px-3 py-4 text-[10px] ${index % 2 ? "bg-blue-50/60" : "bg-white"}`}>
              {row.map((cell) => <span key={cell} className="font-semibold text-slate-800">{cell}</span>)}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <Info label="Visible Drop Note" value="Finished height + hem logic" />
        <Info label="Fabric Source" value="New cut and reusable half pieces" />
        <Info label="Workshop note" value="Check shade before cutting" />
      </div>
      <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm font-semibold text-amber-900">
        Check fabric shade before cutting. Confirm track brackets for ceiling mount.
      </div>
    </div>
  );
}

function OptimizerPdfPreview() {
  const cuts = [
    ["A", 7.2, "#38bdf8"],
    ["B", 5.4, "#60a5fa"],
    ["C", 4.8, "#818cf8"],
    ["D", 3.6, "#22c55e"],
  ];
  return (
    <div className="mx-auto max-w-[900px] rounded-[24px] bg-white p-4 shadow-2xl sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-700">Roll Optimizer PDF</div>
          <h3 className="mt-2 text-3xl font-black tracking-[-0.05em]">Fabric planning clarity</h3>
          <p className="mt-1 text-slate-500">Client A / Texture Linen 09 / Quote FQ-2026-041</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-900">Waste reduced by 18%</div>
      </div>
      <div className="mt-8 grid gap-5">
        {["Roll A - 30m", "Roll B - 22m"].map((roll, rollIndex) => (
          <div key={roll} className="rounded-2xl border border-slate-200 p-5">
            <div className="mb-4 flex justify-between text-sm font-black"><span>{roll}</span><span>{rollIndex ? "Leftover 3.2m" : "Leftover 2.6m"}</span></div>
            <div className="flex h-12 overflow-hidden rounded-2xl bg-slate-100 sm:h-16">
              {cuts.slice(rollIndex, rollIndex + 3).map(([label, width, color]) => (
                <div key={label} style={{ flex: width, background: color }} className="grid place-items-center border-r border-white/60 text-xs font-black text-white">
                  Cut {label}
                </div>
              ))}
              <div className="grid flex-[2.6] place-items-center bg-slate-300 text-xs font-black text-slate-700">Leftover</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-4">
        <Info label="Required" value="41.8 m" />
        <Info label="Allocated" value="44.4 m" />
        <Info label="Reusable" value="5.8 m" />
        <Info label="Waste" value="1.9 m" />
      </div>
      <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold text-emerald-950">
        Half-piece reuse is tracked with the workshop sheet so cutting and material planning stay connected.
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-black text-slate-950">{value}</div>
    </div>
  );
}

function GenericCrmSection() {
  return (
    <section className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <SectionHeader
          eyebrow="Why generic CRMs fail"
          title="A normal CRM does not understand fabric, stitching, workshop handoff, or installation reality."
          text="FabriQuote is not a generic sales board with a quotation attachment. It is built from the workflow furnishing businesses repeat every day."
        />
        <GlassCard className="p-7 sm:p-9">
          <div className="grid gap-4">
            {[
              ["Generic CRM", "Lead status only. No quote logic, PDF output, roll planning, or workshop handoff."],
              ["Billing app", "Invoice totals only. No furnishing workflow, follow-up, team coordination, or production visibility."],
              ["FabriQuote", "Quotation + CRM + PDF + workshop + optimizer + dashboard in one connected system."],
            ].map(([title, text], index) => (
              <div key={title} className={`rounded-2xl border p-5 ${index === 2 ? "border-cyan-300/30 bg-cyan-300/10" : "border-white/10 bg-white/[0.045]"}`}>
                <h3 className="text-xl font-black">{title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-300">{text}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </section>
  );
}

function RoiSection() {
  return (
    <section id="roi" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader eyebrow="ROI" title="Give the owner back control, time, and margin." center />
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} className="mt-14 grid gap-5 md:grid-cols-3">
        {[
          ["12+", "hours saved weekly", "Less rebuilding quotes and searching chats."],
          ["18%", "waste visibility", "Roll planning exposes waste before cutting."],
          ["100%", "workflow clarity", "Lead, quote, workshop, and installation stay connected."],
        ].map(([number, label, text]) => (
          <GlassCard key={label} className="p-8 text-center">
            <div className="text-6xl font-black tracking-[-0.07em] text-cyan-200">
              <CountUp end={Number(number.replace(/\D/g, ""))} duration={1.8} suffix={number.includes("%") ? "%" : "+"} enableScrollSpy />
            </div>
            <h3 className="mt-4 text-2xl font-black">{label}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-300">{text}</p>
          </GlassCard>
        ))}
      </motion.div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader eyebrow="Trusted by furnishing businesses" title="Built around how furnishing businesses actually operate." />
      <div className="mt-12 grid gap-5 lg:grid-cols-3">
        {testimonials.map(([name, role, quote]) => (
          <GlassCard key={name} className="p-8">
            <Sparkles className="h-6 w-6 text-cyan-300" />
            <p className="mt-6 text-lg leading-8 text-slate-200">"{quote}"</p>
            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="font-black">{name}</div>
              <div className="mt-1 text-sm text-slate-400">{role}</div>
            </div>
          </GlassCard>
        ))}
      </div>
    </section>
  );
}

function TrialSection({
  trialForm,
  trialErrors,
  trialSubmitState,
  handleTrialFieldChange,
  submitTrialRequest,
  openBookDemo,
  fullNameInputRef,
  salesWhatsappHref,
  salesWhatsappMessageHref,
  salesEmail,
  salesPhone,
}) {
  return (
    <section id="trial" className="mx-auto max-w-[1480px] px-5 py-32 sm:px-8 lg:px-10">
      <GlassCard className="p-7 sm:p-10">
        <div className="grid gap-10 lg:grid-cols-[0.62fr_0.38fr]">
          <div>
            <p className="mb-5 text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Trial + onboarding</p>
            <h2 className="text-[clamp(2.5rem,5vw,5.2rem)] font-black leading-[0.92] tracking-[-0.07em]">Try FabriQuote properly, not blindly.</h2>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300">Start with 10 free quotations. No payment gateway required. Continue by contacting our sales team after your free usage is complete. Zoom onboarding is included so your team can test quotation, CRM, PDFs, workshop handoff, and roll planning using your real process.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                [Play, "Start free trial"],
                [BellRing, "Book onboarding"],
                [Settings2, "Configure workflow"],
                [TrendingUp, "Start quoting same day"],
              ].map(([Icon, text]) => (
                <div key={text} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.045] p-4">
                  <Icon className="h-5 w-5 text-cyan-300" />
                  <span className="font-bold">{text}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-slate-300">
              <span className="rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 font-semibold">WhatsApp: +91 77366 20477</span>
              <a href={`tel:${salesPhone}`} className="rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 font-semibold transition hover:border-cyan-300/40">
                Call {salesPhone}
              </a>
            </div>
          </div>
          <form onSubmit={submitTrialRequest} className="rounded-[30px] border border-white/10 bg-[#020712]/55 p-6">
            <h3 className="text-3xl font-black tracking-[-0.05em]">Start your free FabriQuote trial</h3>
            <p className="mt-3 text-sm leading-7 text-slate-300">Create up to 10 quotations free. No payment gateway required.</p>
            <div className="mt-6 grid gap-3">
              {[
                ["fullName", "Full name"],
                ["businessName", "Business name"],
                ["phone", "Phone"],
                ["email", "Email"],
              ].map(([field, label]) => (
                <div key={field}>
                  <input
                    ref={field === "fullName" ? fullNameInputRef : null}
                    value={trialForm[field]}
                    onChange={handleTrialFieldChange(field)}
                    placeholder={label}
                    className={`w-full rounded-2xl border px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/40 ${
                      trialErrors[field] ? "border-rose-400/60 bg-rose-500/10" : "border-white/10 bg-white/[0.06]"
                    }`}
                  />
                  {trialErrors[field] ? <p className="mt-2 text-sm text-rose-300">{trialErrors[field]}</p> : null}
                </div>
              ))}
              <select
                value={trialForm.businessType}
                onChange={handleTrialFieldChange("businessType")}
                className="rounded-2xl border border-white/10 bg-[#111827] px-4 py-3 text-white outline-none focus:border-cyan-300/40"
              >
                <option>Curtain showroom</option>
                <option>Sofa / furniture business</option>
                <option>Wallpaper / interiors</option>
                <option>Full furnishing business</option>
              </select>
              <button
                type="submit"
                disabled={trialSubmitState.status === "submitting"}
                className="mt-2 rounded-full bg-gradient-to-r from-cyan-200 via-sky-300 to-blue-500 px-6 py-4 font-black text-slate-950 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {trialSubmitState.status === "submitting" ? "Submitting..." : "Start with 10 Free Quotations"}
              </button>
              <button
                type="button"
                disabled={trialSubmitState.status === "submitting"}
                onClick={openBookDemo}
                className="rounded-full border border-white/10 bg-white/[0.05] px-6 py-4 font-black text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {trialSubmitState.status === "submitting" && trialSubmitState.requestType === "demo" ? "Submitting..." : "Book Zoom Demo"}
              </button>
              {trialSubmitState.message ? (
                <div
                  className={`rounded-3xl border px-4 py-4 text-sm leading-7 ${
                    trialSubmitState.status === "success"
                      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                      : "border-rose-400/30 bg-rose-500/10 text-rose-100"
                  }`}
                >
                  <p>{trialSubmitState.message}</p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    {salesWhatsappHref ? (
                      <a
                        href={salesWhatsappMessageHref}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 font-bold text-cyan-100 underline underline-offset-4"
                      >
                        WhatsApp FabriQuote <ArrowRight className="h-4 w-4" />
                      </a>
                    ) : (
                      <a
                        href={`mailto:${salesEmail}`}
                        className="inline-flex items-center gap-2 font-bold text-cyan-100 underline underline-offset-4"
                      >
                        Email {salesEmail} <ArrowRight className="h-4 w-4" />
                      </a>
                    )}
                    <a
                      href={`mailto:${salesEmail}`}
                      className="inline-flex items-center gap-2 font-bold text-white underline underline-offset-4"
                    >
                      Email {salesEmail} <ArrowRight className="h-4 w-4" />
                    </a>
                    {trialSubmitState.status === "success" ? (
                      <a
                        href="https://quote.fabriquote.in/login"
                        className="inline-flex items-center gap-2 font-bold text-white underline underline-offset-4"
                      >
                        Login <ArrowRight className="h-4 w-4" />
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </form>
        </div>
      </GlassCard>
    </section>
  );
}

function FAQ() {
  return (
    <section className="mx-auto max-w-[1100px] px-5 py-32 sm:px-8 lg:px-10">
      <SectionHeader eyebrow="FAQ" title="Questions serious furnishing owners ask before switching." center />
      <div className="mt-12 grid gap-4">
        {faqs.map(([q, a]) => (
          <GlassCard key={q} className="p-6">
            <h3 className="text-xl font-black">{q}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-300">{a}</p>
          </GlassCard>
        ))}
      </div>
    </section>
  );
}

function FinalCTA({ scrollToTrial, openBookDemo }) {
  return (
    <section className="mx-auto max-w-[1480px] px-5 pb-28 pt-20 sm:px-8 lg:px-10">
      <div className="rounded-[48px] border border-cyan-300/20 bg-[radial-gradient(circle_at_50%_0%,rgba(56,189,248,0.22),transparent_45%),rgba(255,255,255,0.055)] px-6 py-20 text-center shadow-[0_0_140px_rgba(56,189,248,0.16)] backdrop-blur-2xl sm:px-10">
        <p className="mb-5 text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Final CTA</p>
        <h2 className="mx-auto max-w-5xl text-[clamp(3rem,6vw,7rem)] font-black leading-[0.9] tracking-[-0.08em]">Stop running your furnishing business through spreadsheets.</h2>
        <p className="mx-auto mt-7 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">FabriQuote brings quotations, CRM, customer PDFs, workshop instructions, roll planning, stock visibility, and reporting into one product built for the way furnishing teams actually operate.</p>
        <div className="mt-9 flex flex-col justify-center gap-4 sm:flex-row">
          <button type="button" onClick={scrollToTrial} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-200 via-sky-300 to-blue-500 px-7 py-4 font-black text-slate-950">Get 10 Free Quotations <ChevronRight className="h-4 w-4" /></button>
          <button type="button" onClick={openBookDemo} className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.06] px-7 py-4 font-black text-white">Book Zoom Demo</button>
        </div>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 text-sm text-slate-300 sm:flex-row">
          <span>WhatsApp: +91 77366 20477</span>
          <a href={`mailto:${DEFAULT_SALES_EMAIL}`} className="hover:text-white">{DEFAULT_SALES_EMAIL}</a>
          <a href={`tel:${DEFAULT_SALES_PHONE}`} className="hover:text-white">{DEFAULT_SALES_PHONE}</a>
        </div>
      </div>
    </section>
  );
}

function FloatingWhatsappButton({ href }) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-3 rounded-full border border-emerald-300/25 bg-emerald-400/90 px-4 py-3 text-sm font-black text-slate-950 shadow-[0_24px_50px_rgba(16,185,129,0.28)] transition hover:-translate-y-1 hover:bg-emerald-300 sm:bottom-7 sm:right-7 sm:px-5"
    >
      <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-950/10 text-base">W</span>
      <span className="hidden sm:inline">Chat on WhatsApp</span>
      <span className="sm:hidden">Chat</span>
    </a>
  );
}
